import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { ImageUpload } from "@/components/upload/ImageUpload";
import { RoomTypeSelector } from "@/components/staging/RoomTypeSelector";
import { StyleSelector } from "@/components/staging/StyleSelector";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { stagingCosts } from "@/data/mockData";
import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from "uuid";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Check, Info, HelpCircle } from "lucide-react";

// Maps for style IDs by room type
const styleIdsByRoom: Record<string, Record<string, string>> = {
  "space-bedroom": {
    "modern": "65e7110b84ad1b973723171a",
    "contemporary": "6665be393f441904a1e3d7be",
    "scandinavian": "65e715b084ad1b973723173e",
    "industrial": "65e7157c84ad1b973723173a",
    "farmhouse": "65e715ed84ad1b9737231748",
    "hampton": "65e713be84ad1b973723171c",
    "traditional": "65e7152d84ad1b9737231732",
  },
  "space-living-room": {
    "modern": "6665c6b33f441904a1e3d7ce",
    "contemporary": "6665c6e13f441904a1e3d7d0",
    "scandinavian": "6665c7f43f441904a1e3d7d2",
    "industrial": "6665c8223f441904a1e3d7d4",
    "farmhouse": "6665c8523f441904a1e3d7d6",
    "hampton": "6665c87c3f441904a1e3d7d8",
    "traditional": "6665c8a23f441904a1e3d7da",
  },
  "space-dining-room": {
    "modern": "6665c18d3f441904a1e3d7c0",
    "contemporary": "6665c2df3f441904a1e3d7c2",
    "scandinavian": "6665c33a3f441904a1e3d7c4",
    "industrial": "6665c36d3f441904a1e3d7c6",
    "farmhouse": "6665c3d53f441904a1e3d7c8",
    "hampton": "6665c4083f441904a1e3d7ca",
    "traditional": "6665c4d03f441904a1e3d7cc",
  },
  "space-home-office": {
    "modern": "6665d4de3f441904a1e3d7dc",
    "contemporary": "6665d8223f441904a1e3d7de",
    "scandinavian": "6665d8483f441904a1e3d7e0",
    "industrial": "6665d8643f441904a1e3d7e2",
    "farmhouse": "6665d8a73f441904a1e3d7e4",
    "hampton": "6665d8fc3f441904a1e3d7e6",
    "traditional": "6665d91b3f441904a1e3d7e8",
  },
  "space-kitchen--": {
    "modern": "6665c18d3f441904a1e3d7c0",
    "contemporary": "6665c2df3f441904a1e3d7c2",
    "scandinavian": "6665c33a3f441904a1e3d7c4",
    "industrial": "6665c36d3f441904a1e3d7c6",
    "farmhouse": "6665c3d53f441904a1e3d7c8",
    "hampton": "6665c4083f441904a1e3d7ca",
    "traditional": "6665c4d03f441904a1e3d7cc",
  },
  "space-single-bedroom": {
    "modern": "66d057c18307570b5fb7b543",
    "contemporary": "66d057ed8307570b5fb7b545",
    "scandinavian": "66d058918307570b5fb7b547",
    "industrial": "66d058c28307570b5fb7b549",
    "farmhouse": "66d059048307570b5fb7b54b",
    "hampton": "66d059278307570b5fb7b54d",
    "traditional": "66d0594e8307570b5fb7b54f",
  },
  "space-outdoor--": {
    "modern": "66d05ad08307570b5fb7b551",
    "contemporary": "66d05b3e8307570b5fb7b553",
    "scandinavian": "66d05b678307570b5fb7b555",
    "industrial": "66d05b8c8307570b5fb7b557",
    "farmhouse": "66d05c4a8307570b5fb7b559",
    "hampton": "66d05f1f8307570b5fb7b55b",
    "traditional": "66d05f6e8307570b5fb7b55d",
  },
  "space-bathroom--": {
    "modern": "65e7110b84ad1b973723171a",
    "contemporary": "6665be393f441904a1e3d7be",
    "scandinavian": "65e715b084ad1b973723173e",
    "industrial": "65e7157c84ad1b973723173a",
    "farmhouse": "65e715ed84ad1b9737231748",
    "hampton": "65e713be84ad1b973723171c",
    "traditional": "65e7152d84ad1b9737231732",
  },
  "space-foyer": {
    "modern": "66d060408307570b5fb7b55f",
    "contemporary": "66d0605e8307570b5fb7b561",
    "scandinavian": "66d0607d8307570b5fb7b563",
    "industrial": "66d060a08307570b5fb7b565",
    "farmhouse": "66d060c38307570b5fb7b567",
    "hampton": "66d060e78307570b5fb7b569",
    "traditional": "66d0610c8307570b5fb7b56b",
  }
};

// Map room types to API space names
const roomTypeToSpaceName: Record<string, string> = {
  "living-room": "space-living-room",
  "bedroom": "space-bedroom",
  "dining-room": "space-dining-room",
  "home-office": "space-home-office",
  "kitchen": "space-kitchen--",
  "single-bedroom": "space-single-bedroom",
  "outdoor": "space-outdoor--",
  "bathroom": "space-bathroom--",
  "foyer": "space-foyer",
};

// Add this utility at the top (or import from utils if you prefer)
function sanitizeFileName(filename: string) {
  // Remove diacritics
  const noDiacritics = filename.normalize("NFD").replace(/[0-]/g, "");
  // Replace spaces with underscores and remove invalid characters
  return noDiacritics
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9._-]/g, "")
    .toLowerCase();
}

const NewProject = () => {
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [selectedRoomType, setSelectedRoomType] = useState("");
  const [selectedStyle, setSelectedStyle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // Step tracking
  const [currentStep, setCurrentStep] = useState(0); // 0: Initial, 1: Image Upload, 2: Order Submission
  
  // API responses
  const [uploadResponse, setUploadResponse] = useState<any>({ message: 'No upload response yet. Submit your image to see the response here.' });
  const [orderResponse, setOrderResponse] = useState<any>({ message: 'No order submission response yet. Submit your image to see the response here.' });
  const [secondOrderResponse, setSecondOrderResponse] = useState<any>(null);
  
  // Dialog controls
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  const [showImageUploadConfirmation, setShowImageUploadConfirmation] = useState(false);
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  
  // Order data storage between steps
  const [orderId, setOrderId] = useState<string | null>(null);
  
  // API request information for display
  const [imageUploadRequestUrl, setImageUploadRequestUrl] = useState("https://api.aihomedesign.com/v1/order/image");
  const [orderSubmissionRequestUrl, setOrderSubmissionRequestUrl] = useState("https://api.aihomedesign.com/v1/order");
  const [imageUploadRequestBody, setImageUploadRequestBody] = useState("No request body yet");
  const [orderSubmissionRequestBody, setOrderSubmissionRequestBody] = useState("No request body yet");
  
  // Confirmation dialog
  const [pendingRequest, setPendingRequest] = useState<any>(null);
  
  // API Reference dialog controls
  const [showApiReference, setShowApiReference] = useState(false);
  
  const { toast } = useToast();
  const { credits, updateCredits, user } = useAuth();
  const navigate = useNavigate();

  // Get the API key from the environment
  const apiKey = import.meta.env.VITE_AI_HOME_API_KEY;

  // Update imageUploadHeaders and orderSubmissionHeaders to use the env API key
  const [imageUploadHeaders, setImageUploadHeaders] = useState({
    "x-api-key": apiKey,
    "Accept": "application/json"
  });
  
  const [orderSubmissionHeaders, setOrderSubmissionHeaders] = useState({
    "x-api-key": apiKey,
    "Content-Type": "application/json",
    "Accept": "application/json"
  });
  
  // Check if user is authenticated
  useEffect(() => {
    const checkUser = async () => {
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        toast({
          variant: "destructive",
          description: "Pro vytvoření projektu se musíte přihlásit"
        });
        navigate('/login');
      }
    };
    
    checkUser();
  }, [navigate, toast]);

  const handleImageUploaded = (file: File, preview: string) => {
    setImage(file);
    setPreview(preview);
  };

  const handleRoomTypeSelect = (roomTypeId: string) => {
    setSelectedRoomType(roomTypeId);
  };

  const handleStyleSelect = (styleId: string) => {
    setSelectedStyle(styleId);
  };

  const getStagingCreditCost = () => {
    return stagingCosts.find(cost => cost.service === "Virtual Staging")?.creditCost || 1;
  };

  // Helper function to convert File to base64 string
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // Get auth token for edge function calls
  const getAuthToken = async (): Promise<string | null> => {
    try {
      const { data } = await supabase.auth.getSession();
      if (!data.session || !data.session.access_token) {
        toast({
          variant: "destructive",
          description: "Authentication token not available. Please sign in again."
        });
        navigate('/login');
        return null;
      }
      return data.session.access_token;
    } catch (error) {
      console.error("Error getting auth token:", error);
      return null;
    }
  };

  // Set up progress simulation
  useEffect(() => {
    let progressInterval: number | null = null;
    
    if (isSubmitting && progress < 90) {
      progressInterval = window.setInterval(() => {
        setProgress((prev) => {
          const newProgress = prev + 5;
          return newProgress < 90 ? newProgress : 90;
        });
      }, 2000);
    }
    
    return () => {
      if (progressInterval) window.clearInterval(progressInterval);
    };
  }, [isSubmitting, progress]);

  // Combine upload and order submission into one function
  const handleFullSubmission = async () => {
    const creditCost = getStagingCreditCost();
    if (credits < creditCost) {
      toast({
        variant: "destructive",
        description: `Nedostatek kreditů! Potřebujete ${creditCost} kreditů.`
      });
      return;
    }
    if (!image) {
      toast({
        variant: "destructive",
        description: "Prosím nahrajte obrázek"
      });
      return;
    }
    if (!selectedRoomType) {
      toast({
        variant: "destructive",
        description: "Prosím vyberte typ místnosti"
      });
      return;
    }
    if (!selectedStyle) {
      toast({
        variant: "destructive",
        description: "Prosím vyberte designový styl"
      });
      return;
    }
    setIsSubmitting(true);
    setProgress(0);
    try {
      // Generate a unique sanitized filename
      const fileName = `${uuidv4()}-${sanitizeFileName(image.name)}`;
      // Convert image to base64 for the edge function
      const base64Image = await convertFileToBase64(image);
      // Upload image to Supabase Storage
      const filePath = `user_uploads/${user?.id}/${Date.now()}-${sanitizeFileName(image.name)}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('room-images')
        .upload(filePath, image);
      if (uploadError) {
        toast({ variant: "destructive", description: `Image upload failed: ${uploadError.message}` });
        setIsSubmitting(false);
        return;
      }
      setProgress(20);
      // Get the public URL for the uploaded image
      const { data: publicUrlData } = supabase.storage
        .from('room-images')
        .getPublicUrl(filePath);
      const publicUrl = publicUrlData.publicUrl;
      setProgress(40);
      // Prevent project creation if publicUrl is missing
      if (!publicUrl) {
        toast({ variant: "destructive", description: "Failed to get public URL for the uploaded image." });
        setIsSubmitting(false);
        return;
      }
      // Prepare API request for order
      const spaceName = roomTypeToSpaceName[selectedRoomType] || "space-living-room";
      const styleMap = styleIdsByRoom[spaceName] || styleIdsByRoom["space-living-room"];
      const styleId = styleMap[selectedStyle.toLowerCase()] || styleMap["modern"];
      // Prepare image blob for API
      const base64Data = base64Image.split(",")[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const imageBlob = new Blob([byteArray], { type: image.type });
      const formData = new FormData();
      formData.append("service_name", "service-ai-virtual-staging");
      formData.append("image", imageBlob, sanitizeFileName(fileName));
      // Upload to AI Home Design API
      const response = await fetch(imageUploadRequestUrl, {
        method: "POST",
        headers: {
          "x-api-key": imageUploadHeaders["x-api-key"],
        },
        body: formData
      });
      setProgress(60);
      if (!response.ok) {
        const errorText = await response.text();
        setIsSubmitting(false);
        toast({ variant: "destructive", description: `Image upload failed: ${errorText}` });
        return;
      }
      const data = await response.json();
      if (!data.order_id) {
        setIsSubmitting(false);
        toast({ variant: "destructive", description: `No order_id returned from API.` });
        return;
      }
      // --- NEW: Download and upload staged image to Supabase Storage ---
      let stagedImageSupabaseUrl = null;
      if (data.staged_image_url) {
        try {
          // Download the staged image as a blob
          const stagedImageResp = await fetch(data.staged_image_url);
          if (stagedImageResp.ok) {
            const stagedBlob = await stagedImageResp.blob();
            const stagedFileName = `${uuidv4()}-staged-${sanitizeFileName(image.name)}`;
            const stagedFilePath = `user_uploads/${user?.id}/${Date.now()}-${stagedFileName}`;
            const { data: stagedUploadData, error: stagedUploadError } = await supabase.storage
              .from('room-images')
              .upload(stagedFilePath, stagedBlob);
            if (!stagedUploadError) {
              const { data: stagedPublicUrlData } = supabase.storage
                .from('room-images')
                .getPublicUrl(stagedFilePath);
              stagedImageSupabaseUrl = stagedPublicUrlData.publicUrl;
            }
          }
        } catch (err) {
          console.error('Failed to download/upload staged image:', err);
        }
      }
      // --- END NEW ---
      // Insert project record in Supabase
      const { error: projectError } = await supabase
        .from("projects")
        .insert({
          user_id: user?.id,
          order_id: data.order_id,
          original_image_url: publicUrl,
          staged_image_url: stagedImageSupabaseUrl,
          room_type: selectedRoomType,
          style_type: selectedStyle,
          status: "pending",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      if (projectError) {
        toast({ 
          variant: "destructive", 
          description: `Failed to create project record: ${projectError.message}` 
        });
        setIsSubmitting(false);
        return;
      }
      // --- NEW: Delete oldest projects if user has more than 50 ---
      const { data: userProjects, error: fetchProjectsError } = await supabase
        .from("projects")
        .select("id, original_image_url")
        .eq("user_id", user?.id)
        .order("created_at", { ascending: false });
      if (!fetchProjectsError && userProjects && userProjects.length > 50) {
        const projectsToDelete = userProjects.slice(50);
        for (const proj of projectsToDelete) {
          // Remove image from Supabase Storage
          if (proj.original_image_url) {
            // Extract the path from the public URL
            const url = new URL(proj.original_image_url);
            const path = url.pathname.replace(/^\/storage\/v1\/object\/public\/room-images\//, "");
            await supabase.storage.from('room-images').remove([path]);
          }
          // Remove project record
          await supabase.from("projects").delete().eq("id", proj.id);
        }
      }
      // --- END NEW LOGIC ---
      // Subtract credits after successful project creation
      const { error: creditError } = await supabase
        .from("user_credits")
        .update({ credits: credits - creditCost })
        .eq("user_id", user?.id);
      if (creditError) {
        toast({
          variant: "destructive",
          description: `Failed to subtract credits: ${creditError.message}`
        });
      }
      // Prepare order submission (always happens)
      const orderBody = {
        order_id: data.order_id,
        space_name: spaceName,
        selected_widgets: [
          {
            id: "65b4e95ec2b80c47dfe933ab",
            item_id: styleId,
          },
        ],
      };
      const orderResponse = await fetch(orderSubmissionRequestUrl, {
        method: "POST",
        headers: {
          "x-api-key": orderSubmissionHeaders["x-api-key"],
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(orderBody)
      });
      setProgress(100);
      if (!orderResponse.ok) {
        const errorText = await orderResponse.text();
        toast({ variant: "destructive", description: `Order submission failed: ${errorText}` });
        setIsSubmitting(false);
        return;
      }
      toast({ description: "Projekt byl úspěšně vytvořen a objednávka odeslána!" });
      setIsSubmitting(false);
      navigate("/projects");
    } catch (error: any) {
      setIsSubmitting(false);
      toast({ variant: "destructive", description: `Submission error: ${error.message}` });
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container py-8">
        <h1 className="text-3xl font-bold mb-6">Vytvořit nový projekt</h1>

        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <div>
            <h2 className="text-xl font-semibold mb-4">1. Nahrajte fotografii místnosti</h2>
            <ImageUpload onImageUploaded={handleImageUploaded} />
          </div>
          
          <div className="space-y-8">
            <div>
              <h2 className="text-xl font-semibold mb-4">2. Vyberte typ místnosti</h2>
              <RoomTypeSelector
                selectedRoomType={selectedRoomType}
                onSelectRoomType={handleRoomTypeSelect}
              />
            </div>
            
            <div>
              <h2 className="text-xl font-semibold mb-4">3. Vyberte designový styl</h2>
              <StyleSelector
                selectedStyle={selectedStyle}
                onSelectStyle={handleStyleSelect}
              />
              {isSubmitting && (
                <div className="mb-8">
                  <p className="text-sm font-medium mb-2">Průběh zpracování:</p>
                  <Progress value={progress} className="h-2 w-full" />
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row items-center justify-between border-t border-b py-6 my-4">
          <div className="mb-4 sm:mb-0">
            <h3 className="text-lg font-medium">Virtuální zařízení</h3>
            <p className="text-gray-500 text-sm">
              Cena: {getStagingCreditCost()} kreditů • Máte: {credits} kreditů
            </p>
          </div>
          <Button 
            size="lg"
            className="min-w-[220px] px-10 py-6 text-xl font-bold"
            disabled={isSubmitting || !image || !selectedRoomType || !selectedStyle || !user}
            onClick={handleFullSubmission}
          >
            {isSubmitting ? "Zpracování..." : `Spustit proces (${getStagingCreditCost()} Kreditů)`}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default NewProject;
